import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import CoursesApi from "../../../api/coursesApi";
import ProjectApi from "../../../api/projectApi";
import QuizApi from "../../../api/quizApi";
import SpecialistApi from "../../../api/specialistsApi";
import { ContextProvider } from "../../../contextProvider";
import { Company, Project } from "../../../types/Company";
import { TypeNewProject, TypeNewTag } from "../../../types/Projects";
import {
  ProjectQuizBucket,
  QueuedProjectQuizClone,
} from "../../../types/Quiz";
import { SessionPackage } from "../../../types/TypeSession";
import { SpecialistLevel, SpecialistTag } from "../../../types/TypeSpecialist";
import { getTranslation } from "../../../utils/getTranslation";
import { SuccesNotify } from "../../../utils/toaster";
import { assertQuizChapters } from "../../../utils/quizChapters";
import {
  createProjectFormModel,
  getProjectSpecializationId,
  getSelectedProjectTypes,
  validateProjectSubmit,
} from "./projectFormModel";
import {
  S3_MULTIPART_THRESHOLD_BYTES,
  uploadMultipartFileToS3,
} from "../../../utils/s3MultipartUpload";
import {
  getFileMimeType,
  uploadStoredFileWithPresignedUrl,
} from "../../../utils/storedFileReferences";

export type ProjectFormMode = "create" | "edit";

type DropDownMenuData = { lable: string; value: string; parentTag?: string };

export type ProjectLevelOption = {
  value: string;
  lable: string;
  specializationId: string;
  specializationLabel: string;
};

export type ProjectFormControllerParams = {
  mode: ProjectFormMode;
  selectedCompany: Company;
  token: string | null;
  project?: Project | null;
  onBeforeSuccessfulNavigate?: () => void;
};

export type ProjectFormController = {
  projectData: TypeNewProject;
  setProjectData: Dispatch<SetStateAction<TypeNewProject>>;
  mainSpecializations: (DropDownMenuData & { specialization: string })[];
  specialistLevels: ProjectLevelOption[];
  selectedProjectTypes: ReturnType<typeof getSelectedProjectTypes>;
  selectedSpecializationIds: string[];
  selectedSpecializationsKey: string;
  projectTags: { [specialization: string]: TypeNewTag[] };
  setProjectTags: Dispatch<
    SetStateAction<{ [specialization: string]: TypeNewTag[] }>
  >;
  selectedCourseIds: string[];
  setSelectedCourseIds: Dispatch<SetStateAction<string[]>>;
  videos: { language: string; video?: File }[];
  setVideoForLanguage: (language: string, video?: File) => void;
  existingVideos: Project["video"];
  existingTags: SpecialistTag[];
  staticPackages: SessionPackage[];
  saveProject: () => Promise<void>;
  isDirty: boolean;
  loading: boolean;
  errorMessage: string;
  clearError: () => void;
};

const getDirtySnapshot = (
  projectData: TypeNewProject,
  projectTags: { [specialization: string]: TypeNewTag[] },
  videos: { language: string; video?: File }[],
  selectedCourseIds: string[],
) =>
  JSON.stringify({
    projectData,
    projectTags,
    selectedCourseIds,
    videos: videos
      .filter((item) => item.video)
      .map((item) => ({
        language: item.language,
        name: item.video?.name,
        size: item.video?.size,
        type: item.video?.type,
        lastModified: item.video?.lastModified,
      })),
  });

const getScopedProjectData = (
  projectData: TypeNewProject,
  levels: ProjectLevelOption[],
  specializationIds: string[],
): TypeNewProject => {
  const levelIds = levels.map((item) => item.value);
  const activeTypes = getSelectedProjectTypes(projectData);

  return {
    ...projectData,
    availableSessionTypes: activeTypes.length
      ? activeTypes.flatMap((type) =>
          specializationIds.map((specialization) => {
            const previous = projectData.availableSessionTypes?.find(
              (item) =>
                item.type === type &&
                getProjectSpecializationId(item.specialization) ===
                  specialization,
            );

            return {
              type,
              specialization,
              amount: previous?.amount ?? 0,
              unlimited: previous?.unlimited ?? true,
            };
          }),
        )
      : projectData.availableSessionTypes,
    pricing: projectData.pricing?.filter((item) =>
      levelIds.includes(item.level),
    ),
    packagePricing: projectData.packagePricing?.filter((item) =>
      levelIds.includes(item.level),
    ),
  };
};

const isQueuedProjectQuizClone = (
  quiz: ProjectQuizBucket["quizes"][number],
): quiz is QueuedProjectQuizClone =>
  typeof quiz === "object" && "isQueuedProjectQuizClone" in quiz;

const getSourceQuizId = (quiz: ProjectQuizBucket["quizes"][number]) => {
  if (typeof quiz === "string") return null;
  if (isQueuedProjectQuizClone(quiz)) return quiz.sourceQuizId;
  if (!quiz.clonedFrom) return quiz._id;
  return typeof quiz.clonedFrom === "string" ? quiz.clonedFrom : quiz.clonedFrom._id;
};

const getProjectDataWithEmptyQuizBuckets = (
  projectData: TypeNewProject,
): TypeNewProject => ({
  ...projectData,
  quizBuckets: projectData.quizBuckets?.map((bucket) => ({
    title: bucket.title,
    activationTime: bucket.activationTime,
    quizes: [],
  })),
});

const getQueuedProjectQuizClones = (projectData: TypeNewProject) =>
  (projectData.quizBuckets ?? []).flatMap((bucket, bucketIndex) =>
    bucket.quizes.flatMap((quiz, quizIndex) =>
      isQueuedProjectQuizClone(quiz)
        ? [
            {
              bucketIndex,
              quizIndex,
              clone: quiz,
            },
          ]
        : [],
    ),
  );

const validateProjectQuizQueue = (projectData: TypeNewProject) => {
  const duplicateBuckets: number[] = [];

  projectData.quizBuckets?.forEach((bucket, bucketIndex) => {
    const sourceIds = bucket.quizes
      .map(getSourceQuizId)
      .filter((sourceId): sourceId is string => Boolean(sourceId));
    const uniqueSourceIds = new Set(sourceIds);
    if (uniqueSourceIds.size !== sourceIds.length) {
      duplicateBuckets.push(bucketIndex + 1);
    }
  });

  return duplicateBuckets;
};

export const useProjectFormController = ({
  mode,
  selectedCompany,
  token,
  project,
  onBeforeSuccessfulNavigate,
}: ProjectFormControllerParams): ProjectFormController => {
  const navigate = useNavigate();
  const { userData } = useContext(ContextProvider);
  const initialModel = useMemo(
    () => createProjectFormModel(project ?? undefined),
    [project],
  );
  const [projectData, setProjectData] = useState(initialModel.projectData);
  const [mainSpecializations, setMainSpecializations] = useState<
    (DropDownMenuData & { specialization: string })[]
  >([]);
  const [specialistLevels, setSpecialistLevels] = useState<
    ProjectLevelOption[]
  >([]);
  const [projectTags, setProjectTags] = useState<{
    [specialization: string]: TypeNewTag[];
  }>({});
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [videos, setVideos] = useState<{ language: string; video?: File }[]>(
    [],
  );
  const [staticPackages, setStaticPackages] = useState<SessionPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [cleanSnapshot, setCleanSnapshot] = useState(() =>
    getDirtySnapshot(initialModel.projectData, {}, [], []),
  );

  useEffect(() => {
    const nextModel = createProjectFormModel(project ?? undefined);
    setProjectData(nextModel.projectData);
    setProjectTags({});
    setSelectedCourseIds([]);
    setVideos([]);
    setCleanSnapshot(getDirtySnapshot(nextModel.projectData, {}, [], []));
  }, [project]);

  const dirtySnapshot = useMemo(
    () =>
      getDirtySnapshot(
        projectData,
        projectTags,
        videos,
        selectedCourseIds,
      ),
    [projectData, projectTags, videos, selectedCourseIds],
  );
  const isDirty = dirtySnapshot !== cleanSnapshot;

  const selectedSpecializationIds = useMemo(
    () =>
      projectData.specializations?.length && mainSpecializations.length
        ? projectData.specializations
        : mainSpecializations.map((item) => item.value),
    [projectData.specializations, mainSpecializations],
  );

  const selectedSpecializationsKey = selectedSpecializationIds.join(",");
  const selectedProjectTypes = useMemo(
    () => getSelectedProjectTypes(projectData),
    [projectData.availableSessionTypes],
  );

  useEffect(() => {
    if (!token) return;
    let ignore = false;

    const loadMainSpecializations = async () => {
      const response = await SpecialistApi.getMainSpecializations(token);
      if (ignore) return;

      if (response?.status && response.mainSpecializations) {
        setMainSpecializations(
          response.mainSpecializations.map((item) => ({
            lable: getTranslation(item.labels, "en"),
            value: item._id,
            specialization: item.specialization,
          })),
        );
      }
    };

    loadMainSpecializations();

    return () => {
      ignore = true;
    };
  }, [token]);

  useEffect(() => {
    if (!selectedSpecializationIds.length) {
      setSpecialistLevels([]);
      return;
    }

    let ignore = false;

    const loadSpecialistLevels = async () => {
      const responses = await Promise.all(
        selectedSpecializationIds.map((mainSpecId) =>
          SpecialistApi.getAllSpecialistLevels({ mainSpecId }),
        ),
      );

      if (ignore) return;

      const levels = responses.flatMap((response) => {
        if (!response?.status || !response.levels?.length) return [];

        return response.levels.map((level: SpecialistLevel) => ({
          value: level._id,
          lable: getTranslation(level.title, "en"),
          specializationId: level.mainSpecialization._id,
          specializationLabel: getTranslation(
            level.mainSpecialization.labels,
            "en",
          ),
        }));
      });

      setSpecialistLevels(levels);
      setProjectData((prev) =>
        getScopedProjectData(prev, levels, selectedSpecializationIds),
      );
    };

    loadSpecialistLevels();

    return () => {
      ignore = true;
    };
  }, [selectedSpecializationsKey]);

  useEffect(() => {
    if (!project?.staticPackages?.length || !token) {
      setStaticPackages([]);
      return;
    }

    let ignore = false;

    const loadStaticPackages = async () => {
      const responses = await Promise.all(
        project.staticPackages!.map((item) =>
          SpecialistApi.getSpecialistSessionPackageById(token, item.package),
        ),
      );

      if (ignore) return;

      if (responses.every((item) => item.status && item.package)) {
        setStaticPackages(responses.map((item) => item.package!));
      }
    };

    loadStaticPackages();

    return () => {
      ignore = true;
    };
  }, [project?.staticPackages, token]);

  const setVideoForLanguage = (language: string, video?: File) => {
    setVideos((prev) => {
      const next = [...prev];
      const existingIndex = next.findIndex(
        (item) => item.language === language,
      );

      if (existingIndex >= 0) {
        next[existingIndex] = { language, video };
      } else {
        next.push({ language, video });
      }

      return next;
    });
  };

  const addProjectTags = async (projectId: string) => {
    if (!token) return undefined;
    const tags = Object.values(projectTags).flat();
    if (!tags.length) return undefined;

    return ProjectApi.addProjectTags(token, {
      projectId,
      tags,
    });
  };

  const uploadProjectVideos = async (projectId: string) => {
    if (!token) return [] as string[];
    const errorMessages: string[] = [];

    for (const videoItem of videos) {
      if (!videoItem.video) continue;
      const videoS3 =
        videoItem.video.size >= S3_MULTIPART_THRESHOLD_BYTES
          ? await uploadProjectVideoWithMultipart(
              projectId,
              videoItem.language,
              videoItem.video,
            )
          : await uploadStoredFileWithPresignedUrl({
              file: videoItem.video,
              getUploadUrl: (body) =>
                ProjectApi.getProjectVideoUploadUrl(token, projectId, {
                  ...body,
                  language: videoItem.language,
                }),
            });

      const videoResponse = await ProjectApi.saveProjectVideo(token, {
        projectId,
        language: videoItem.language,
        videoS3,
      });

      if (!videoResponse.status) {
        errorMessages.push(
          videoResponse.message || "Failed to upload Project video.",
        );
      }
    }

    return errorMessages;
  };

  const uploadProjectVideoWithMultipart = async (
    projectId: string,
    language: string,
    file: File,
  ) => {
    const initiateResponse =
      await ProjectApi.initiateProjectVideoMultipartUpload(token!, projectId, {
        language,
        originalName: file.name,
        mimeType: getFileMimeType(file),
        size: file.size,
      });

    if (
      !initiateResponse.status ||
      !initiateResponse.uploadId ||
      !initiateResponse.filepath
    ) {
      throw new Error(initiateResponse.message ?? "Failed to prepare upload");
    }

    const multipartUpload = await uploadMultipartFileToS3({
      file,
      uploadId: initiateResponse.uploadId,
      filepath: initiateResponse.filepath,
      getPartUrl: async (partNumber) => {
        const response = await ProjectApi.generateProjectVideoPartUrl(
          token!,
          projectId,
          {
            language,
            filepath: initiateResponse.filepath!,
            uploadId: initiateResponse.uploadId!,
            partNumber,
          },
        );

        return response.status ? response.url : undefined;
      },
      complete: async (parts) => {
        const response = await ProjectApi.completeProjectVideoMultipartUpload(
          token!,
          projectId,
          {
            language,
            filepath: initiateResponse.filepath!,
            uploadId: initiateResponse.uploadId!,
            parts,
          },
        );

        if (!response.status) {
          throw new Error(response.message ?? "Failed to complete upload");
        }
      },
      uploadErrorMessage: "Failed to upload Project video.",
      uploadLinkErrorMessage: "Failed to prepare Project video upload.",
    });

    return multipartUpload.filepath;
  };

  const cloneSelectedCourses = async (projectId: string) => {
    if (!token || !selectedCourseIds.length) return [] as string[];
    const errorMessages: string[] = [];

    for (const courseId of selectedCourseIds) {
      const response = await CoursesApi.cloneCourse(token, {
        courseId,
        project: projectId,
        ...(userData?._id ? { createdBy: userData._id } : {}),
      });

      if (!response.status) {
        errorMessages.push(
          response.message || "Failed to add a Course to the Project.",
        );
      }
    }

    return errorMessages;
  };

  const cloneQueuedProjectQuizzes = async (
    projectId: string,
    sourceProjectData: TypeNewProject,
  ) => {
    if (!token) return [] as string[];
    const queuedClones = getQueuedProjectQuizClones(sourceProjectData);
    const errorMessages: string[] = [];

    for (const item of queuedClones) {
      const response = await QuizApi.cloneQuiz(token, {
        quizId: item.clone.sourceQuizId,
        refs: { project: projectId },
        placement: {
          bucketIndex: item.bucketIndex,
          quizIndex: item.quizIndex,
        },
        projectConfig: {
          isConfidential: item.clone.isConfidential,
          isEditable: item.clone.isEditable,
          isScoreShown: item.clone.isScoreShown,
        },
      });

      if (!response.status) {
        errorMessages.push(
          `Bucket ${item.bucketIndex + 1}, position ${item.quizIndex + 1}: ${
            response.message || "Failed to clone queued Project Quiz."
          }`,
        );
        continue;
      }
      try {
        if (!response.quiz) {
          throw new Error("Backend did not return the authoritative cloned Quiz. Reopen the Project before retrying.");
        }
        assertQuizChapters(response.quiz.quiz);
        const clonedQuiz = response.quiz;
        setProjectData((previous) => ({
          ...previous,
          quizBuckets: previous.quizBuckets?.map((bucket) => ({
            ...bucket,
            quizes: bucket.quizes.map((quiz) =>
              typeof quiz !== "string" && quiz._id === item.clone._id
                ? clonedQuiz
                : quiz,
            ),
          })),
        }));
      } catch (error) {
        errorMessages.push(
          `Bucket ${item.bucketIndex + 1}, position ${item.quizIndex + 1}: ${
            error instanceof Error ? error.message : "Invalid cloned Quiz response."
          }`,
        );
      }
    }

    return errorMessages;
  };

  const getProjectSavePayload = () =>
    mode === "create"
      ? getProjectDataWithEmptyQuizBuckets(projectData)
      : projectData;

  const saveProject = async () => {
    if (!token) {
      setErrorMessage("Authentication token is missing. Please sign in again.");
      return;
    }

    const validation = validateProjectSubmit(projectData);
    if (!validation.isValid) {
      setErrorMessage(
        `Required fields missing: ${validation.missingFields.join(", ")}.`,
      );
      return;
    }

    if (mode === "edit" && !project) {
      setErrorMessage("Project data is missing for edit mode.");
      return;
    }

    try {
      projectData.quizBuckets?.forEach((bucket) => {
        bucket.quizes.forEach((quiz) => {
          if (typeof quiz !== "string") {
            assertQuizChapters(
              isQueuedProjectQuizClone(quiz) ? quiz.sourceQuiz.quiz : quiz.quiz,
            );
          }
        });
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Invalid Project Quiz configuration.");
      return;
    }

    const duplicateQuizQueueBuckets = validateProjectQuizQueue(projectData);
    if (duplicateQuizQueueBuckets.length) {
      setErrorMessage(
        `Each bucket can contain a System Quiz only once. Check bucket${duplicateQuizQueueBuckets.length === 1 ? "" : "s"} ${duplicateQuizQueueBuckets.join(", ")}.`,
      );
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const projectSavePayload = getProjectSavePayload();
      const response =
        mode === "create"
          ? await ProjectApi.createProject(token, {
              companyId: selectedCompany._id,
              newProject: projectSavePayload,
            })
          : await ProjectApi.update(token, {
              _id: project!._id,
              ...projectSavePayload,
            });

      if (!response.status || !response.project) {
        setErrorMessage(
          response.message ||
            `Failed to ${mode === "create" ? "create" : "update"} the Project.`,
        );
        return;
      }

      const postSaveErrors: string[] = [];
      const tagsResponse = await addProjectTags(response.project._id);
      if (tagsResponse && !tagsResponse.status) {
        postSaveErrors.push(
          tagsResponse.message || "Failed to save Project tags.",
        );
      }

      postSaveErrors.push(
        ...(await cloneSelectedCourses(response.project._id)),
      );
      postSaveErrors.push(
        ...(mode === "create"
          ? await cloneQueuedProjectQuizzes(response.project._id, projectData)
          : []),
      );
      postSaveErrors.push(...(await uploadProjectVideos(response.project._id)));

      if (postSaveErrors.length) {
        const queuedCloneErrors = postSaveErrors.filter((message) =>
          message.startsWith("Bucket "),
        );
        setErrorMessage(
          queuedCloneErrors.length
            ? [
                `Project was created (${response.project._id}), but part of the Project Quiz setup failed. No rollback was performed. Reopen this Project for editing to finish setup.`,
                ...postSaveErrors,
              ].join("\n")
            : postSaveErrors.join("\n"),
        );
        return;
      }

      SuccesNotify(
        mode === "create"
          ? "Project has been created"
          : "Project has been updated",
      );
      setCleanSnapshot(
        getDirtySnapshot(
          projectData,
          projectTags,
          videos,
          selectedCourseIds,
        ),
      );
      onBeforeSuccessfulNavigate?.();
      navigate(`/companies?companyId=${selectedCompany._id}&tab=projects`);
    } catch (error) {
      console.error("Error saving Project:", error);
      setErrorMessage("Something went wrong while saving the Project.");
    } finally {
      setLoading(false);
    }
  };

  return {
    projectData,
    setProjectData,
    mainSpecializations,
    specialistLevels,
    selectedProjectTypes,
    selectedSpecializationIds,
    selectedSpecializationsKey,
    projectTags,
    setProjectTags,
    selectedCourseIds,
    setSelectedCourseIds,
    videos,
    setVideoForLanguage,
    existingVideos: initialModel.existingVideos,
    existingTags: initialModel.existingTags,
    staticPackages,
    saveProject,
    isDirty,
    loading,
    errorMessage,
    clearError: () => setErrorMessage(""),
  };
};
